import React, { useMemo, useState } from 'react';
import {
  Empty,
  Modal,
  Segmented,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useDevMode } from '@/contexts/DevModeContext';
import { COLORS, formatUnit } from '../../../utils/constants';
import {
  runCostPerMetre,
  RUN_MATRIX_DISCIPLINE_LABEL,
  type RunMatrixCellLine,
  type RunMatrixColumn,
  type RunMatrixDiscipline,
  type RunMatrixResult,
  type RunMatrixRow,
} from '../../../utils/runMatrixGenerator';
import type { SectionMatrix } from '../../../utils/siteWorksMatrixGenerator';
import SectionMatrixTable from './SectionMatrixTable';

const { Text, Paragraph } = Typography;

const fmtCurrency = (n: number): string =>
  `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtNumber = (n: number, dp = 2): string =>
  n.toLocaleString('en-GB', { minimumFractionDigits: dp, maximumFractionDigits: dp });

/** Column / grand totals are 2dp-rounded before display, same as the Plot Matrix. */
const round2 = (n: number): number => Math.round(n * 100) / 100;

type DisciplineFilter = 'all' | RunMatrixDiscipline;

interface CellModalState {
  row: RunMatrixRow;
  column: RunMatrixColumn;
}

interface DrainageRunMatrixProps {
  matrix: RunMatrixResult;
  /** Pump Station + Rising Main — a sub-tab under Foul Sewers. */
  foulBuildUps: SectionMatrix;
  /** Ponds & Swales, Cellular / Weholite / Conc attenuation, Box Culverts — a sub-tab under Storm Sewers. */
  stormBuildUps: SectionMatrix;
  loading?: boolean;
}

/** Within one discipline: the runs grid, or that discipline's build-ups. */
type DisciplineView = 'runs' | 'build-ups';

/**
 * The drainage half of the Run Matrix — one row per manhole-to-manhole pipe run
 * (Foul then Storm), one column per drainage cost category. Built by
 * `buildRunMatrix` in the parent so the parent can also price the other sections
 * with the same markup; this component owns only its own view state (discipline
 * filter, search, heat map, cell breakdown).
 */
const DrainageRunMatrix: React.FC<DrainageRunMatrixProps> = ({
  matrix,
  foulBuildUps,
  stormBuildUps,
  loading = false,
}) => {
  const showDevMode = useDevMode();
  const [disciplineFilter, setDisciplineFilter] = useState<DisciplineFilter>('all');
  const [disciplineView, setDisciplineView] = useState<DisciplineView>('runs');
  const [cellModal, setCellModal] = useState<CellModalState | null>(null);

  // "All" spans both disciplines, so there is no single build-up set to show —
  // switching to it always lands back on the runs grid.
  const activeBuildUps = disciplineFilter === 'foul' ? foulBuildUps : stormBuildUps;
  const showingBuildUps = disciplineFilter !== 'all' && disciplineView === 'build-ups';

  // ── Discipline filter ─────────────────────────────────────────────────────
  // Separator rows only survive when the section below them still has rows, so
  // filtering never leaves an orphaned section heading behind.
  const visibleRows = useMemo(() => {
    const kept = matrix.rows.filter(
      (row) =>
        row.rowKind === 'separator' ||
        disciplineFilter === 'all' ||
        row.discipline === disciplineFilter,
    );
    return kept.filter((row, i) => {
      if (row.rowKind !== 'separator') return true;
      const next = kept[i + 1];
      return !!next && next.rowKind !== 'separator';
    });
  }, [matrix.rows, disciplineFilter]);

  const dataRows = useMemo(
    () => visibleRows.filter((row) => row.rowKind !== 'separator'),
    [visibleRows],
  );

  const visibleColumnTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    matrix.columns.forEach((col) => {
      totals[col.key] = dataRows.reduce(
        (sum, row) => sum + (row.cells[col.key]?.total ?? 0),
        0,
      );
    });
    return totals;
  }, [dataRows, matrix.columns]);

  const visibleTotal = useMemo(
    () => dataRows.reduce((sum, row) => sum + row.total, 0),
    [dataRows],
  );

  const visibleLength = useMemo(
    () => dataRows.filter((r) => r.rowKind === 'run').reduce((sum, row) => sum + row.length, 0),
    [dataRows],
  );

  const visibleSiteWide = useMemo(
    () => dataRows.filter((r) => r.rowKind === 'site-wide').reduce((s, r) => s + r.total, 0),
    [dataRows],
  );

  const hasSewerData = matrix.rows.some((row) => row.rowKind !== 'separator');
  const totalVariance =
    matrix.reconciliation.foul.variance + matrix.reconciliation.storm.variance;

  // ── Table columns ─────────────────────────────────────────────────────────
  const leafCount = 5 + matrix.columns.length + 2;

  const renderSeparatorCell = (record: RunMatrixRow) =>
    record.rowKind === 'separator' ? { colSpan: leafCount } : {};

  const hiddenOnSeparator = (record: RunMatrixRow) =>
    record.rowKind === 'separator' ? { colSpan: 0 } : {};

  const categoryColumns = useMemo(() => {
    const groups: Array<{ group: string; columns: RunMatrixColumn[] }> = [];
    matrix.columns.forEach((col) => {
      const last = groups[groups.length - 1];
      if (last && last.group === col.group) last.columns.push(col);
      else groups.push({ group: col.group, columns: [col] });
    });

    return groups.map((group) => ({
      title: group.group,
      key: `group-${group.group}`,
      align: 'center' as const,
      children: group.columns.map((col) => ({
        title: (
          <Tooltip title={col.description}>
            <span style={{ color: '#1890ff', fontWeight: 600 }}>{col.label}</span>
          </Tooltip>
        ),
        key: col.key,
        width: 130,
        align: 'right' as const,
        onCell: (record: RunMatrixRow) =>
          record.rowKind === 'separator' ? { colSpan: 0 } : {},
        render: (_: unknown, record: RunMatrixRow) => {
          if (record.rowKind === 'separator') return null;
          const cell = record.cells[col.key];
          const value = cell?.total ?? 0;
          if (value === 0) return <Text type="secondary">—</Text>;
          return (
            <Text
              style={{
                cursor: 'pointer',
                textDecoration: 'underline dotted',
                textUnderlineOffset: 3,
                color: value < 0 ? '#cf1322' : undefined,
              }}
              onClick={() => setCellModal({ row: record, column: col })}
            >
              {fmtCurrency(value)}
            </Text>
          );
        },
      })),
    }));
  }, [matrix.columns]);

  const columns: ColumnsType<RunMatrixRow> = [
    {
      title: 'Drainage Run',
      key: 'run-group',
      align: 'center' as const,
      children: [
        {
          title: 'Run',
          dataIndex: 'label',
          key: 'label',
          width: 150,
          fixed: 'left' as const,
          onCell: renderSeparatorCell,
          render: (_: unknown, record: RunMatrixRow) => {
            if (record.rowKind === 'separator') {
              return (
                <strong
                  style={{ fontSize: 13, color: COLORS.primary, letterSpacing: '0.05em' }}
                >
                  {RUN_MATRIX_DISCIPLINE_LABEL[record.discipline].toUpperCase()}
                </strong>
              );
            }
            if (record.rowKind === 'site-wide') {
              return (
                <Tooltip title="Manually-entered items that belong to the scheme rather than to any one run — EO items, redundant sewer, Clegg testing, road-construction credits.">
                  <Text italic type="secondary">
                    Not run-specific <InfoCircleOutlined />
                  </Text>
                </Tooltip>
              );
            }
            return <Text strong>{record.label}</Text>;
          },
        },
        {
          title: 'Pipe Ø',
          dataIndex: 'diameter',
          key: 'diameter',
          width: 90,
          align: 'center' as const,
          onCell: hiddenOnSeparator,
          render: (value: string, record: RunMatrixRow) =>
            record.rowKind === 'run' && value ? value : '',
        },
        {
          title: 'Type',
          dataIndex: 'pipeType',
          key: 'pipeType',
          width: 100,
          align: 'center' as const,
          onCell: hiddenOnSeparator,
          render: (value: string | undefined, record: RunMatrixRow) =>
            record.rowKind === 'run' && value ? (
              <Text style={{ textTransform: 'capitalize' }}>{value}</Text>
            ) : (
              ''
            ),
        },
        {
          title: 'Length (m)',
          dataIndex: 'length',
          key: 'length',
          width: 100,
          align: 'right' as const,
          onCell: hiddenOnSeparator,
          render: (value: number, record: RunMatrixRow) =>
            record.rowKind === 'run' ? fmtNumber(value) : '',
        },
        {
          title: (
            <Tooltip title="Run depth category — average depth to invert, rounded to the nearest 0.25m band the rates are priced on.">
              <span>dti (m)</span>
            </Tooltip>
          ),
          dataIndex: 'depth',
          key: 'depth',
          width: 90,
          align: 'right' as const,
          onCell: hiddenOnSeparator,
          render: (value: number, record: RunMatrixRow) =>
            record.rowKind === 'run' ? fmtNumber(value) : '',
        },
      ],
    },
    ...categoryColumns,
    {
      title: 'Run Total',
      key: 'run-total',
      align: 'center' as const,
      children: [
        {
          title: 'Total (£)',
          key: 'total',
          width: 140,
          align: 'right' as const,
          onCell: (record: RunMatrixRow) =>
            record.rowKind === 'separator' ? { colSpan: 0 } : {},
          render: (_: unknown, record: RunMatrixRow) =>
            record.rowKind === 'separator' ? null : (
              <Text strong style={{ color: record.total < 0 ? '#cf1322' : undefined }}>
                {fmtCurrency(record.total)}
              </Text>
            ),
        },
        {
          title: (
            <Tooltip title="Run total divided by run length — the quickest way to spot an out-of-line run.">
              <span>£ / m</span>
            </Tooltip>
          ),
          key: 'costPerMetre',
          width: 110,
          align: 'right' as const,
          onCell: hiddenOnSeparator,
          render: (_: unknown, record: RunMatrixRow) => {
            const perMetre = runCostPerMetre(record);
            return perMetre === null ? (
              <Text type="secondary">—</Text>
            ) : (
              <Text>{fmtCurrency(perMetre)}</Text>
            );
          },
        },
      ],
    },
  ];

  // ── Breakdown modal ───────────────────────────────────────────────────────
  const cellLines: RunMatrixCellLine[] = cellModal
    ? cellModal.row.cells[cellModal.column.key]?.lines ?? []
    : [];

  const lineColumns: ColumnsType<RunMatrixCellLine & { key: number }> = [
    {
      title: 'Item',
      dataIndex: 'description',
      key: 'description',
      render: (value: string, record: RunMatrixCellLine) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Text>{value}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {record.source}
            {record.share !== undefined && ` · ${(record.share * 100).toFixed(1)}% of item`}
          </Text>
          {showDevMode && record.evaluateCode && (
            <Text style={{ fontSize: 10, color: '#999' }}>{record.evaluateCode}</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Qty',
      dataIndex: 'qty',
      key: 'qty',
      width: 100,
      align: 'right',
      render: (value: number) => fmtNumber(value),
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 70,
      align: 'center',
      render: (value: string) => formatUnit(value) || '',
    },
    {
      title: 'Rate (£)',
      dataIndex: 'rate',
      key: 'rate',
      width: 110,
      align: 'right',
      render: (value: number) => fmtNumber(value),
    },
    {
      title: 'Amount (£)',
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      align: 'right',
      render: (value: number) => <Text strong>{fmtCurrency(value)}</Text>,
    },
  ];

  if (!hasSewerData) {
    return (
      <div style={{ padding: '32px' }}>
        <Empty
          description={
            <span>
              No foul or storm sewer take-off entered yet. Add pipe runs on the Foul
              Sewers / Storm Sewers pages and the Run Matrix will build itself.
            </span>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      <style>{`
        .run-matrix-table .ant-table { border-radius: 0 !important; }
        .run-matrix-table .ant-table-container { border-radius: 0 !important; }
        .run-matrix-table .ant-table-thead > tr > th {
          background-color: ${COLORS.tableHeaderBg} !important;
          text-align: center;
        }
        .run-matrix-table .ant-table-tbody > tr.run-matrix-separator > td {
          background-color: #d1d5db !important;
          padding-top: 6px !important;
          padding-bottom: 6px !important;
        }
        .run-matrix-table .ant-table-tbody > tr.run-matrix-site-wide > td {
          background-color: #f0f4ff !important;
        }
        .run-matrix-table .ant-table-tbody > tr.run-matrix-site-wide:hover > td {
          background-color: #e5ecff !important;
        }
      `}</style>

      {/* ── Headline figures ───────────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          gap: 24,
          flexWrap: 'wrap',
          padding: '12px 16px',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          background: '#fbfcfe',
          marginBottom: 16,
        }}
      >
        <Statistic
          title="Runs"
          value={dataRows.filter((r) => r.rowKind === 'run').length}
          valueStyle={{ fontSize: 20 }}
        />
        <Statistic
          title="Run length"
          value={fmtNumber(visibleLength)}
          suffix="m"
          valueStyle={{ fontSize: 20 }}
        />
        <Statistic
          title="Matrix total"
          value={fmtCurrency(visibleTotal)}
          valueStyle={{ fontSize: 20, color: COLORS.primary }}
        />
        <Statistic
          title="Average"
          value={visibleLength > 0 ? `${fmtCurrency(visibleTotal / visibleLength)}/m` : '—'}
          valueStyle={{ fontSize: 20 }}
        />
        <Statistic
          title="Not run-specific"
          value={fmtCurrency(visibleSiteWide)}
          valueStyle={{ fontSize: 20, color: visibleSiteWide === 0 ? undefined : '#d46b08' }}
        />
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip
            title={
              Math.abs(totalVariance) < 0.01
                ? 'Every priced Foul and Storm Bill Summary item is accounted for in this matrix.'
                : `The matrix is out by ${fmtCurrency(totalVariance)} against the Bill Summary totals.`
            }
          >
            <Tag color={Math.abs(totalVariance) < 0.01 ? 'success' : 'error'}>
              {Math.abs(totalVariance) < 0.01
                ? 'Ties to Bill Summary'
                : `Variance ${fmtCurrency(totalVariance)}`}
            </Tag>
          </Tooltip>
        </div>
      </div>

      {/* ── Toolbar ────────────────────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <Space size={12} wrap>
          <Segmented
            value={disciplineFilter}
            onChange={(value) => {
              setDisciplineFilter(value as DisciplineFilter);
              if (value === 'all') setDisciplineView('runs');
            }}
            options={[
              { label: 'All', value: 'all' },
              { label: 'Foul Sewers', value: 'foul' },
              { label: 'Storm Sewers', value: 'storm' },
            ]}
          />
          {disciplineFilter !== 'all' && (
            <Segmented
              value={disciplineView}
              onChange={(value) => setDisciplineView(value as DisciplineView)}
              options={[
                { label: 'Drainage Runs', value: 'runs' },
                { label: activeBuildUps.title, value: 'build-ups' },
              ]}
            />
          )}
        </Space>

      </div>

      {showingBuildUps ? (
        <SectionMatrixTable matrix={activeBuildUps} loading={loading} />
      ) : (
      <div className="run-matrix-table">
        {loading ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '80px 0',
              gap: 16,
            }}
          >
            <Spin size="large" />
            <span style={{ color: '#6B7280', fontSize: 14 }}>Loading rate library…</span>
          </div>
        ) : (
        <Table<RunMatrixRow>
          columns={columns}
          dataSource={visibleRows}
          rowKey="key"
          bordered
          size="middle"
          pagination={false}
          scroll={{ x: 'max-content' }}
          sticky={{ offsetHeader: 80 }}
          rowClassName={(record) =>
            record.rowKind === 'separator'
              ? 'run-matrix-separator'
              : record.rowKind === 'site-wide'
              ? 'run-matrix-site-wide'
              : ''
          }
          locale={{
            emptyText: <Empty description="No runs match the current filter" />,
          }}
          summary={() => (
            <Table.Summary.Row style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
              <Table.Summary.Cell index={0} colSpan={3}>
                <strong>GRAND TOTAL</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right">
                <strong>{fmtNumber(visibleLength)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} />
              {matrix.columns.map((col, i) => (
                <Table.Summary.Cell key={col.key} index={5 + i} align="right">
                  <strong>{fmtCurrency(round2(visibleColumnTotals[col.key] ?? 0))}</strong>
                </Table.Summary.Cell>
              ))}
              <Table.Summary.Cell index={5 + matrix.columns.length} align="right">
                <strong style={{ fontSize: 16 }}>{fmtCurrency(round2(visibleTotal))}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6 + matrix.columns.length} align="right">
                <strong>
                  {visibleLength > 0 ? fmtCurrency(visibleTotal / visibleLength) : '—'}
                </strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
        )}
      </div>
      )}

      {/* ── Cell breakdown ─────────────────────────────────────────────────── */}
      <Modal
        open={!!cellModal}
        onCancel={() => setCellModal(null)}
        footer={null}
        width={860}
        title={
          cellModal
            ? `${
                cellModal.row.rowKind === 'site-wide'
                  ? 'Not run-specific'
                  : cellModal.row.label
              } · ${cellModal.column.label}`
            : ''
        }
      >
        {cellModal && (
          <>
            <Paragraph type="secondary" style={{ fontSize: 13 }}>
              {cellModal.column.description}
            </Paragraph>
            <Table<RunMatrixCellLine & { key: number }>
              size="small"
              bordered
              pagination={false}
              dataSource={cellLines.map((line, i) => ({ ...line, key: i }))}
              columns={lineColumns}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={4}>
                    <Text strong>Cell total</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong>
                      {fmtCurrency(cellModal.row.cells[cellModal.column.key]?.total ?? 0)}
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
            {cellLines.some((line) => line.share !== undefined) && (
              <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12 }}>
                Lines showing a percentage are this run&apos;s share of a Bill Summary item
                that covers several runs — the split follows the same per-run breakdown the
                Bill Summary&apos;s formula drawer shows.
              </Paragraph>
            )}
          </>
        )}
      </Modal>

    </div>
  );
};

export default DrainageRunMatrix;
